import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { format, getMonth, getYear, addMonths, isSameDay, isBefore, isAfter } from "date-fns";
import _ from "lodash";

import { LrdReportFrequency, normalizeDate } from "../constants";

import arrowBackIcon from "./arrow-back-ios.svg";
import arrowForwardIcon from "./arrow-forward-ios.svg";
import { generateMonth } from "./util/helper";
import "./styles.css";

const CalendarSectionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.5em;
  height: 342px;
`;

const CalendarWrapper = styled.div`
  min-height: 320px;
  width: 296px;
  border: 1px solid #dadce0;
  border-radius: 8px;
  box-shadow: 0px 10px 20px rgba(173, 174, 174, 0.25);
  padding: 0 0.5em;
  padding-bottom: 12px;
  background-color: #ffffff;
`;

const CalendarMonthText = styled.div`
  font-family: Work Sans;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  text-transform: uppercase;
  padding: 1em 0;
  color: #5f6368;
`;

const HeaderWeek = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  cursor: default;
  margin-bottom: 12px;
`;

const HeaderWeekDay = styled.div`
  text-align: center;
  font-family: Work Sans;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
`;

const StyledWeek = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  padding: 0.2em 0;
  & > *:nth-child(-n + 7) {
    padding: 5px;
  }
`;

const StyledDay = styled.div`
  font-family: Roboto Mono;
  font-size: 14px;
  font-weight: 400;
  text-align: center;
  cursor: ${({ isClickable }) => (isClickable ? "pointer" : "default")};
  border-radius: 8px;
`;

const IconWrapper = styled.div`
  height: inherit;
  display: flex;
  align-items: center;
  margin: ${({ direction }) => (direction === "back" ? "0 0.5em 0 0" : "0 0 0 0.5em")};
  & img {
    cursor: pointer;
  }
`;

const CalendarKeyBarWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin: 2em 0 0 6em;
  font-family: Work Sans;
  font-size: 14px;
  font-weight: 400;
`;

const CalendarKeyColorBox = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background-color: ${({ color }) => color};
  margin-right: 0.5em;
`;

const CalendarKeyItem = styled.div`
  display: flex;
  align-items: center;
  margin-right: 2em;
`;

const ArrowIcon = ({ direction, onClick }) => {
  const icon = direction === "back" ? arrowBackIcon : arrowForwardIcon;

  return (
    <IconWrapper>
      <img src={icon} onClick={onClick} alt={`arrow-${direction}-icon`} />
    </IconWrapper>
  );
};

const CalendarKeyBar = () => (
  <CalendarKeyBarWrapper>
    <CalendarKeyItem>
      <CalendarKeyColorBox color="#DADCE0" />
      <div>Report due</div>
    </CalendarKeyItem>
    <CalendarKeyItem>
      <CalendarKeyColorBox color="#C40A0A" />
      <div>Report overdue</div>
    </CalendarKeyItem>
    <CalendarKeyItem>
      <CalendarKeyColorBox color="#005AA9" />
      <div>Selected date</div>
    </CalendarKeyItem>
  </CalendarKeyBarWrapper>
);

const calendarWeekDaysHeader = ["M", "T", "W", "T", "F", "S", "S"];

const Calendar = ({ selectedDate, setSelectedDate, reportGroupInstances }) => {
  const currentDate = new Date();

  const defaultArrayOfDates = useMemo(
    () => [
      {
        month: addMonths(currentDate, -1),
        monthDates: generateMonth(addMonths(currentDate, -1))
      },
      {
        month: currentDate,
        monthDates: generateMonth(currentDate)
      },
      {
        month: addMonths(currentDate, 1),
        monthDates: generateMonth(addMonths(currentDate, 1))
      }
    ],
    [reportGroupInstances]
  );

  const [dueDates, setDueDates] = useState([]);
  const [arrayOfDates, setArrayOfDates] = useState(defaultArrayOfDates);

  useEffect(() => {
    const filteredOutOldRgis = reportGroupInstances.filter(x => new Date(x.reportingStartDate) <= currentDate);
    const overDueRgis = [];
    const dueRgis = [];
    for (const rgi of filteredOutOldRgis) {
      if (!rgi.isSubmitted && !isAfter(normalizeDate(rgi?.dueDate), currentDate)) {
        overDueRgis.push(rgi);
      } else {
        dueRgis.push(rgi);
      }
    }

    const groupedDeals = _.groupBy(dueRgis, "dealName");

    const currentMonthlyRgi = Object.entries(groupedDeals)
      .map((x) => x[1].find((y) => !y.isOverdue && y.reportingFrequencyId === LrdReportFrequency.MONTHLY))
      .flat()
      .filter((x) => x !== undefined);
    const currentBiMonthlyRgi = Object.entries(groupedDeals)
      .map((x) => x[1].find((y) => !y.isOverdue && y.reportingFrequencyId === LrdReportFrequency.BI_MONTHLY))
      .flat()
      .filter((x) => x !== undefined);
    const currentQuarterlyRgi = Object.entries(groupedDeals)
      .map((x) => x[1].find((y) => !y.isOverdue && y.reportingFrequencyId === LrdReportFrequency.QUARTERLY))
      .flat()
      .filter((x) => x !== undefined);
    const currentSemiAnnualRgi = Object.entries(groupedDeals)
      .map((x) => x[1].find((y) => !y.isOverdue && y.reportingFrequencyId === LrdReportFrequency.SEMI_ANNUALLY))
      .flat()
      .filter((x) => x !== undefined);
    const currentAnnualRgi = Object.entries(groupedDeals)
      .map((x) => x[1].find((y) => !y.isOverdue && y.reportingFrequencyId === LrdReportFrequency.ANNUALLY))
      .flat()
      .filter((x) => x !== undefined);

    const rgisToShow = [];

    if (overDueRgis.length) {
      rgisToShow.push(...overDueRgis);
    }
    if (currentMonthlyRgi) {
      rgisToShow.push(...currentMonthlyRgi);
    }
    if (currentBiMonthlyRgi) {
      rgisToShow.push(...currentBiMonthlyRgi);
    }
    if (currentQuarterlyRgi) {
      rgisToShow.push(...currentQuarterlyRgi);
    }
    if (currentSemiAnnualRgi) {
      rgisToShow.push(...currentSemiAnnualRgi);
    }
    if (currentAnnualRgi) {
      rgisToShow.push(...currentAnnualRgi);
    }

    const rgiDueDates = rgisToShow.map(rgi => rgi.dueDate);
    setDueDates(rgiDueDates);
  }, [reportGroupInstances]);

  const generateNewSetOfMonths = direction => {
    const newArrayOfDates = [...arrayOfDates];
    const monthOffset = 3;
    for (const group of newArrayOfDates) {
      group.month = direction === "back" ? addMonths(group.month, monthOffset * -1) : addMonths(group.month, monthOffset);
      group.monthDates = generateMonth(group.month);
    }
    setArrayOfDates(newArrayOfDates);
  };

  const isDayInMonth = (month, day) => {
    const currentMonth = getMonth(month);
    const monthOfDay = getMonth(day);

    return monthOfDay === currentMonth;
  };

  const determineStyle = (day, month) => {
    const selectedMonth = getMonth(month);
    const monthOfDay = getMonth(day);
    const isSelectedDayInMonth = selectedMonth === monthOfDay;
    const isSelectedDay = isSameDay(selectedDate, day) && isSelectedDayInMonth;
    const isDueDate = dueDates?.some(
      dueDate => isSameDay(normalizeDate(dueDate), normalizeDate(day)) && !isBefore(normalizeDate(day), normalizeDate(currentDate))
    );
    const isOverdue = dueDates?.some(
      dueDate => isSameDay(normalizeDate(dueDate), normalizeDate(day)) && isBefore(normalizeDate(dueDate), normalizeDate(currentDate))
    );

    let className = "";

    if (isSelectedDay && isOverdue) {
      className = `${className} lrd-calendar-selected-day`;
    } else if (isSelectedDay && isDueDate) {
      className = `${className} lrd-calendar-selected-day`;
    } else if (!isSelectedDayInMonth) {
      className = `${className} lrd-calendar-day-not-in-month`;
    } else if (isDueDate) {
      className = `${className} lrd-calendar-day-due-date`;
    } else if (isOverdue) {
      className = `${className} lrd-calendar-day-overdue`;
    } else if (isSelectedDay) {
      className = `${className} lrd-calendar-selected-day`;
    } else {
      className = `${className}`;
    }

    return className;
  };

  const isClickedDateActive = day => dueDates?.some(dueDate => isSameDay(normalizeDate(dueDate), normalizeDate(day)));

  return (
    <div>
      <CalendarSectionWrapper>
        <ArrowIcon direction="back" onClick={() => generateNewSetOfMonths("back")} />
        {arrayOfDates.map(({ month, monthDates }, idx) => (
          <CalendarWrapper key={idx}>
            <CalendarMonthText>
              {format(month, "MMMM")}
              {" "}
              {getYear(month)}
            </CalendarMonthText>
            <HeaderWeek>
              {calendarWeekDaysHeader.map(day => (
                <HeaderWeekDay key={day}>{day}</HeaderWeekDay>
              ))}
            </HeaderWeek>
            <div>
              {monthDates.map((week, index) => (
                <StyledWeek key={index}>
                  {week.map(day => (
                    <StyledDay
                      key={day}
                      className={`${determineStyle(day, month)}`}
                      onClick={() => isClickedDateActive(day) && setSelectedDate(day)}
                      isDayInMonth={isDayInMonth(month, day)}
                      isClickable={isClickedDateActive(day)}
                    >
                      {format(day, "d")}
                    </StyledDay>
                  ))}
                </StyledWeek>
              ))}
            </div>
          </CalendarWrapper>
        ))}
        <ArrowIcon direction="forward" onClick={() => generateNewSetOfMonths("forward")} />
      </CalendarSectionWrapper>
      <CalendarKeyBar />
    </div>
  );
};

export default Calendar;
